import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { MdArrowBack, MdRefresh } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import Card from 'components/card/Card';
import { PageHeader, PageShell } from 'components/layout/PageShell';
import Map from 'components/map/components/StaticMap';
import ListingItemsSection from 'domains/listing/components/ListingItemsSection';
import ListingImageGallery from 'domains/listing/components/ListingImageGallery';
import ListingLoaCard from 'domains/listing/components/ListingLoaCard';
import TransportPlanCard from 'domains/listing/components/TransportPlanCard';
import DocumentList from 'components/documents/DocumentList';
import { getListingById } from 'domains/listing/services/listingService';
import { useTranslation } from 'i18n/TranslationProvider';
import API from 'api/api';
import { mapTemplateTitleToTranslation } from 'domains/shared/utils/categoryUtils';

function formatDate(dateStr, t, language) {
  if (!dateStr) return t('Flexible');
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return t('Flexible');
  return date.toLocaleDateString(language || undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateRange(from, to, t, language) {
  if (!from && !to) return t('Flexible dates');
  if (from && to && from !== to) return `${formatDate(from, t, language)} - ${formatDate(to, t, language)}`;
  return formatDate(from || to, t, language);
}

function formatDistanceKm(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? `${numeric} km` : null;
}

export default function AdminListingDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { t, language } = useTranslation();
  const cancelRef = useRef(null);

  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.200');
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const titleColor = useColorModeValue('navy.800', 'white');
  const routeCardBg = useColorModeValue('blue.50', 'whiteAlpha.100');

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [quotes, setQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);

  const [canceling, setCanceling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const loadListing = async () => {
    setLoading(true);
    setError('');
    try {
      const payload = await getListingById(id);
      const nextListing = payload?.listing ?? payload;
      setListing(nextListing);
    } catch {
      setListing(null);
      setError(t('Could not load this listing.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const payload = await getListingById(id);
        if (active) setListing(payload?.listing ?? payload);
      } catch {
        if (active) {
          setListing(null);
          setError(t('Could not load this listing.'));
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => { active = false; };
  }, [id, t]);

  useEffect(() => {
    if (!listing) return;
    const fetchQuotes = async () => {
      setLoadingQuotes(true);
      try {
        const res = await API.get(`/listings/${id}/quotes`);
        setQuotes(Array.isArray(res.data) ? res.data : []);
      } catch {
        setQuotes([]);
      } finally {
        setLoadingQuotes(false);
      }
    };
    fetchQuotes();
  }, [id, listing]);

  const handleRefresh = () => {
    loadListing();
  };

  const handleCancel = async () => {
    setCanceling(true);
    try {
      await API.delete(`/listings/${id}`);
      toast({ title: t('Listing canceled'), status: 'success', duration: 2500 });
      navigate('/admin/listings');
    } catch {
      toast({ title: t('Failed to cancel listing'), status: 'error', duration: 3000 });
    } finally {
      setCanceling(false);
      setShowCancelDialog(false);
    }
  };

  const details = useMemo(() => {
    if (!listing) return [];
    return [
      { label: t('Reference'), value: listing.humanId || listing.reference || '-' },
      { label: t('Category'), value: listing.category ? t(listing.category).replace(/_/g, ' ') : '-' },
      { label: t('Client type'), value: listing.clientType ? t(listing.clientType) : '-' },
      { label: t('Company country'), value: listing.companyCountry || '-' },
      { label: t('Status'), value: listing.status ? t(listing.status) : '-' },
      { label: t('Distance'), value: formatDistanceKm(listing.distanceKm ?? listing.route?.distanceKm) || '-' },
      { label: t('Created'), value: formatDate(listing.createdAt, t, language) },
      { label: t('Expires'), value: formatDate(listing.expiresAt, t, language) },
    ];
  }, [language, listing, t]);

  if (loading) {
    return (
      <PageShell>
        <Flex justify="center" align="center" minH="40vh">
          <Spinner size="lg" color="blue.400" />
        </Flex>
      </PageShell>
    );
  }

  if (error || !listing) {
    return (
      <PageShell>
        <VStack align="stretch" spacing={4}>
          <Button
            alignSelf="flex-start"
            size="sm"
            variant="ghost"
            leftIcon={<MdArrowBack />}
            onClick={() => navigate('/admin/listings')}
          >
            {t('Back to listings')}
          </Button>
          <Text color="red.400">{error || t('Listing not found.')}</Text>
        </VStack>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <VStack align="stretch" spacing={6}>
        <Flex align="center" justify="space-between">
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<MdArrowBack />}
            onClick={() => navigate('/admin/listings')}
          >
            {t('Back to listings')}
          </Button>
          <HStack spacing={2}>
            <IconButton
              icon={<Icon as={MdRefresh} />}
              aria-label={t('Refresh')}
              variant="ghost"
              size="sm"
              isLoading={loading}
              onClick={handleRefresh}
              borderRadius="xl"
            />
            {listing.status !== 'canceled' && listing.status !== 'completed' && (
              <Button
                colorScheme="red"
                borderRadius="xl"
                size="sm"
                onClick={() => setShowCancelDialog(true)}
              >
                {t('Cancel Listing')}
              </Button>
            )}
          </HStack>
        </Flex>

        <PageHeader
          title={mapTemplateTitleToTranslation(listing.title,t) || t('Listing details')}
          description={
            listing.humanId
              ? t('Reference {{id}}', { values: { id: listing.humanId } })
              : t('Read-only listing overview for admins.')
          }
        />

        <Card border="1px solid" borderColor={borderColor} p={{ base: 5, md: 6 }} borderRadius="24px">
          <HStack spacing={3} flexWrap="wrap" mb={5}>
            <Badge colorScheme="blue" borderRadius="full" px={3} py={1}>
              {t(listing.category || 'other').replace(/_/g, ' ')}
            </Badge>
            <Badge colorScheme={listing.status === 'canceled' ? 'red' : 'gray'} borderRadius="full" px={3} py={1}>
              {t(listing.status || 'unknown').replace(/_/g, ' ')}
            </Badge>
          </HStack>

          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4} mb={6}>
            <Box p={4} borderRadius="20px" bg={routeCardBg} border="1px solid" borderColor="blue.100">
              <Text fontSize="xs" fontWeight="700" color="blue.500" textTransform="uppercase" mb={1}>
                {t('Pick-up')}
              </Text>
              <Text fontWeight="800" color={titleColor}>
                {[listing.fromAddress, listing.fromCity, listing.fromCountry].filter(Boolean).join(', ') || '-'}
              </Text>
              <Text fontSize="sm" color={mutedText} mt={1}>
                {formatDateRange(listing.pickupDateFrom, listing.pickupDateTo, t, language)}
              </Text>
            </Box>
            <Box p={4} borderRadius="20px" bg={routeCardBg} border="1px solid" borderColor="blue.100">
              <Text fontSize="xs" fontWeight="700" color="blue.500" textTransform="uppercase" mb={1}>
                {t('Delivery')}
              </Text>
              <Text fontWeight="800" color={titleColor}>
                {[listing.toAddress, listing.toCity, listing.toCountry].filter(Boolean).join(', ') || '-'}
              </Text>
              <Text fontSize="sm" color={mutedText} mt={1}>
                {formatDateRange(listing.deliveryDateFrom, listing.deliveryDateTo, t, language)}
              </Text>
            </Box>
          </Grid>

          <Box h={{ base: '240px', md: '320px' }} borderRadius="22px" overflow="hidden" bg="gray.100" mb={6}>
            {listing.fromCoords || listing.toCoords ? (
              <Map
                origin={listing.fromCoords}
                destination={listing.toCoords}
                center={{ lat: 50.5, lng: 10.5 }}
                zoom={6}
                width={800}
                height={400}
                fitBoundsPadding={72}
                maxAutoZoom={12}
                mapContainerStyle={{ width: '100%', height: '100%' }}
              />
            ) : (
              <Flex h="100%" align="center" justify="center" px={6} textAlign="center">
                <Text color={mutedText} fontSize="sm">
                  {t('No exact coordinates are stored for this listing yet.')}
                </Text>
              </Flex>
            )}
          </Box>

          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }} gap={4}>
            {details.map((detail) => (
              <Box key={detail.label}>
                <Text fontSize="xs" fontWeight="700" color={mutedText} textTransform="uppercase" mb={1}>
                  {detail.label}
                </Text>
                <Text fontWeight="700" color={titleColor} textTransform={detail.label === t('Category') || detail.label === t('Status') ? 'capitalize' : 'none'}>
                  {detail.value}
                </Text>
              </Box>
            ))}
          </Grid>

          <Divider my={6} />

          <ListingItemsSection
            items={listing.items}
            category={listing.category}
            subtitle={t('Structured overview of quantities and handling details.')}
          />

          <Divider my={6} />

          <ListingImageGallery
            images={listing.images}
            title={t('Shipment images')}
            emptyText={t('No shipment images were uploaded for this listing.')}
          />

          <Divider my={6} />

          <TransportPlanCard transportPlan={listing.transportPlan} />

          <Divider my={6} />

          <ListingLoaCard listingId={listing.id || listing._id} loa={listing.loa} />

          <Divider my={6} />

          <Box>
            <Text fontSize="xs" fontWeight="700" color={mutedText} textTransform="uppercase" mb={3}>
              {t('Proof of pickup')}
            </Text>
            <DocumentList entityType="listing" entityId={listing.id || listing._id} docType="proof_pickup" currentRole="admin" resolveRole={false} />
          </Box>

          <Divider my={6} />

          <Box>
            <Text fontSize="xs" fontWeight="700" color={mutedText} textTransform="uppercase" mb={3}>
              {t('Final documents')}
            </Text>
            <DocumentList entityType="listing" entityId={listing.id || listing._id} docType="cmr" currentRole="admin" resolveRole={false} />
            <Box mt={3}>
              <DocumentList entityType="listing" entityId={listing.id || listing._id} docType="proof_delivery" currentRole="admin" resolveRole={false} />
            </Box>
          </Box>

          {listing.description ? (
            <>
              <Divider my={6} />
              <Box>
                <Text fontSize="xs" fontWeight="700" color={mutedText} textTransform="uppercase" mb={1}>
                  {t('Description')}
                </Text>
                <Text color={titleColor}>{listing.description}</Text>
              </Box>
            </>
          ) : null}

          <Divider my={6} />

          <Box>
            <Text fontSize="xs" fontWeight="700" color={mutedText} textTransform="uppercase" mb={3}>
              {t('Quotes ({{count}})', { values: { count: quotes.length } })}
            </Text>
            {loadingQuotes ? (
              <Text fontSize="sm" color={mutedText}>{t('Loading quotes...')}</Text>
            ) : quotes.length === 0 ? (
              <Text fontSize="sm" color={mutedText}>{t('No quotes for this listing.')}</Text>
            ) : (
              <VStack align="stretch" spacing={3}>
                {quotes.map((quote) => {
                  const quoteId = quote._id || quote.id;
                  const pName = quote.partnerName || quote.partner?.name || quote.partnerId || '—';
                  const score = quote.partnerScore;
                  const gross = quote.grossPrice ?? (quote.netPrice && quote.vatRate != null ? (quote.netPrice * (1 + quote.vatRate / 100)) : null);
                  const status = (quote.status || '').toLowerCase();
                  return (
                    <Box key={quoteId} p={4} border="1px solid" borderColor={borderColor} borderRadius="16px">
                      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={3}>
                        <VStack align="flex-start" spacing={1}>
                          <HStack spacing={2}>
                            <Text fontWeight="700" color={titleColor}>{pName}</Text>
                            {score != null && (
                              <Badge colorScheme="teal" borderRadius="full" px={2}>
                                {t('Score: {{score}}/100', { values: { score } })}
                              </Badge>
                            )}
                            <Badge
                              colorScheme={
                                status === 'accepted' ? 'green' :
                                status === 'rejected' ? 'red' :
                                status === 'expired' ? 'gray' : 'blue'
                              }
                              borderRadius="full"
                              px={2}
                            >
                              {t(status)}
                            </Badge>
                          </HStack>
                          <HStack spacing={2} fontSize="sm" color={mutedText}>
                            {quote.proposedPickupDate && <Text>{t('Pickup: {{date}}', { values: { date: formatDate(quote.proposedPickupDate, t, language) } })}</Text>}
                            {quote.proposedDeliveryDate && <Text>{t('Delivery: {{date}}', { values: { date: formatDate(quote.proposedDeliveryDate, t, language) } })}</Text>}
                          </HStack>
                          {quote.description && <Text fontSize="sm" color={mutedText}>{quote.description}</Text>}
                        </VStack>
                        <Text fontSize="xl" fontWeight="800" color="blue.500">
                          {gross != null ? `€${Number(gross).toFixed(2)}` : '€—'}
                        </Text>
                      </Flex>
                    </Box>
                  );
                })}
              </VStack>
            )}
          </Box>
        </Card>
      </VStack>

      <AlertDialog
        isOpen={showCancelDialog}
        leastDestructiveRef={cancelRef}
        onClose={() => canceling ? null : setShowCancelDialog(false)}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent borderRadius="2xl">
          <AlertDialogHeader fontSize="lg" fontWeight="800">{t('Cancel Listing?')}</AlertDialogHeader>
          <AlertDialogBody>
            {t('This will permanently cancel the listing and notify the client. Continue?')}
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={() => setShowCancelDialog(false)} isDisabled={canceling}>
              {t('Keep')}
            </Button>
            <Button colorScheme="red" ml={3} onClick={handleCancel} isLoading={canceling} loadingText={t('Canceling...')}>
              {t('Cancel Listing')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  );
}
